import React from 'react';
import {Helmet} from 'react-helmet-async';

interface SEOProps {
    productName: string;
    price: number;
    description?: string;
}

export const SEO: React.FC<SEOProps> = ({
        productName,
        price,
        description = 'Get access to Viu Premium (Australia) content with our 1-Day Pass. Watch your favorite shows and movies in HD quality with no ads.'
    }) => {
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: productName,
        description: description,
        offers: {
            '@type': 'Offer',
            price: price,
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
            seller: {
                '@type': 'Organization',
                name: 'Appvanz Pty Ltd',
                url: 'https://viu.appvanz.au'
            }
        }
    };

    const breadcrumbData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                item: {
                    '@id': 'https://viu.appvanz.au',
                    name: 'Home'
                }
            },
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `https://viu.appvanz.au/${productName.toLowerCase().replace(/\s+/g, '-')}`,
                    name: productName
                }
            }
        ]
    };

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{productName} | Appvanz | Viu Authorized Reseller</title>
            <meta name="description" content={description}/>
            <meta name="keywords" content="Viu, Viu Premium, Viu Pass, Streaming, Korean Drama, Asian Drama"/>
            <link rel="canonical" href={`https://viu.appvanz.au/${productName.toLowerCase().replace(/\s+/g, '-')}`} />

            {/* Open Graph Meta Tags */}
            <meta property="og:title" content={productName}/>
            <meta property="og:description" content={description}/>
            <meta property="og:image" content={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/viu-vouchers-banner-og.jpg`}/>
            <meta property="og:image:width" content="1200"/>
            <meta property="og:image:height" content="617"/>
            <meta property="og:type" content="product"/>
            <meta property="og:price:amount" content={price.toString()}/>
            <meta property="og:price:currency" content="AUD"/>
            <meta property="og:url" content={`https://viu.appvanz.au/${productName.toLowerCase().replace(/\s+/g, '-')}`} />
            <meta property="og:site_name" content="Appvanz" />

            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image"/>
            <meta name="twitter:title" content={productName}/>
            <meta name="twitter:description" content={description}/>
            <meta name="twitter:image" content={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/viu-vouchers-banner-og.jpg`}/>

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(breadcrumbData)}
            </script>
        </Helmet>
    );
}; 